import BigNumber from 'bignumber.js'
import { BLOCKS_PER_YEAR } from 'config'
import { BIG_ZERO } from 'utils/bigNumber'
import { getBalanceAmount } from 'utils/formatBalance'
import { useVvsPerBlock } from 'state/farms/useVvsPerBlock'
import { ONE_DAY_UNIX } from 'config/constants/info'
import lpAprs from 'config/constants/lpAprs.json'
import { TOKEN_PRICE_MAPPING } from 'config/constants/tokenPriceMappings'

/**
 * Get the APR value in %
 * @param stakingTokenPrice Token price in the same quote currency
 * @param rewardTokenPrice Token price in the same quote currency
 * @param totalStaked Total amount of stakingToken in the pool
 * @param tokenPerBlock Amount of new VVS allocated to the pool for each new block
 * @returns Null if the APR is NaN or infinite.
 */
export const getPoolApr = (
  stakingTokenPrice: number,
  rewardTokenPrice: number,
  totalStaked: number,
  tokenPerBlock: number,
): number => {
  const totalRewardPricePerYear = new BigNumber(rewardTokenPrice).times(tokenPerBlock).times(BLOCKS_PER_YEAR)
  const totalStakingTokenInPool = new BigNumber(stakingTokenPrice).times(totalStaked)
  // const totalStakingTokenInPool = new BigNumber(stakingTokenPrice).times(1000000000) // XXX debug hard code for APR
  const apr = totalRewardPricePerYear.div(totalStakingTokenInPool).times(100)
  return apr.isNaN() || !apr.isFinite() ? null : apr.toNumber()
}

/**
 * Get farmV1 APR value in %
 * @param poolWeight allocationPoint / totalAllocationPoint
 * @param vvsPriceUsd VVS price in USD
 * @param poolLiquidityUsd Total pool liquidity in USD
 * @param farmAddress Farm Address
 * @returns Farm Apr
 */
export const getFarmApr = (
  poolWeight: BigNumber,
  vvsPriceUsd: BigNumber,
  poolLiquidityUsd: BigNumber,
  farmAddress: string,
): { vvsRewardsApr: number; lpRewardsApr: number } => {
  const yearlyVvsRewardAllocation: BigNumber = poolWeight
    ? poolWeight.times(useVvsPerBlock.getState().value.vvsPerYear)
    : new BigNumber(NaN)
  const vvsRewardsApr: BigNumber = yearlyVvsRewardAllocation.times(vvsPriceUsd).div(poolLiquidityUsd).times(100)
  let vvsRewardsAprAsNumber = null
  if (!vvsRewardsApr.isNaN() && vvsRewardsApr.isFinite()) {
    vvsRewardsAprAsNumber = vvsRewardsApr.toNumber()
  }
  const lpRewardsApr = lpAprs[farmAddress?.toLocaleLowerCase()] ?? 0
  return { vvsRewardsApr: vvsRewardsAprAsNumber, lpRewardsApr }
}

// total tokens allocation eg (token1 + token2 + token3 allocation) / pool liquidity
/**
 * Get farmV2 APR value in %
 * @param poolWeight allocationPoint / totalAllocationPoint
 * @param vvsPriceUsd VVS price in USD
 * @param rewardData Reward Data from rewarder contract
 * @param poolLiquidityUsd Total pool liquidity in USD
 * @param stakedLiquidityInUSD Staked liquidity in USD
 * @param farmAddress Farm Address
 * @param tokenPrices In a LP, each token price in USD
 * @returns Farm Apr
 */
const getFarmAprV2 = (
  vvsPoolWeight: BigNumber,
  vvsPriceUsd: BigNumber,
  rewardData: any[],
  poolLiquidityUsd: BigNumber,
  stakedLiquidityInUSD: BigNumber,
  farmAddress: string,
  tokenPrices: Record<string, number>,
): { vvsRewardsApr: number; tokenRewardsApr: number; lpRewardsApr: number } => {
  const yearlyVvsRewardAllocation: BigNumber = vvsPoolWeight
    ? vvsPoolWeight.times(useVvsPerBlock.getState().value.vvsPerYear)
    : new BigNumber(NaN)
  const vvsRewards = yearlyVvsRewardAllocation.times(vvsPriceUsd)

  const now = Date.now() / 1000
  const vvsRewardsUsd = vvsRewards
  let otherTokenRewardsUsd = BIG_ZERO
  if (rewardData) {
    otherTokenRewardsUsd = rewardData
      .map((data) => {
        const weight: BigNumber = new BigNumber(data.allocPoint).div(data.totalAllocPoint)
        const yearlyReward: BigNumber = getBalanceAmount(data.totalRewardPerSecond, data.token.decimals)
          .times(ONE_DAY_UNIX)
          .times(365)
        const rewardAllocation: BigNumber =
          parseInt(data.rewardStartTimestamp) <= now && now <= parseInt(data.rewardEndTimestamp)
            ? weight.times(yearlyReward)
            : BIG_ZERO

        const address = TOKEN_PRICE_MAPPING[data.token.address.toLowerCase()] ?? data.token.address

        // token price
        const tokenPriceUsd = tokenPrices[address.toLowerCase()] ?? 'NaN'

        return rewardAllocation.times(tokenPriceUsd)
      })
      .reduce((prev, curr) => prev.plus(curr), otherTokenRewardsUsd)
  }

  const vvsRewardsApr: BigNumber = vvsRewardsUsd.div(poolLiquidityUsd).times(100)
  const otherRewardsApr: BigNumber = otherTokenRewardsUsd.div(stakedLiquidityInUSD).times(100)
  const totalRewardsApr: BigNumber = vvsRewardsApr.plus(otherRewardsApr)

  let vvsRewardsAprAsNumber = null
  if (!vvsRewardsApr.isNaN() && vvsRewardsApr.isFinite()) {
    vvsRewardsAprAsNumber = vvsRewardsApr.toNumber()
  }
  let totalRewardsAprAsNumber = null
  if (!totalRewardsApr.isNaN() && totalRewardsApr.isFinite()) {
    totalRewardsAprAsNumber = totalRewardsApr.toNumber()
  }
  const lpRewardsApr = lpAprs[farmAddress?.toLocaleLowerCase()] ?? 0
  return { vvsRewardsApr: vvsRewardsAprAsNumber, tokenRewardsApr: totalRewardsAprAsNumber, lpRewardsApr }
}

/**
 * Get farm APR value in % according to its version (V1/V2)
 * @param poolWeight allocationPoint / totalAllocationPoint
 * @param vvsPriceUsd VVS price in USD
 * @param poolLiquidityUsd Total pool liquidity in USD
 * @param farmAddress Farm Address
 * @param version Farm version
 * @param rewardData Reward Data from rewarder contract (V2 Only)
 * @param stakedLiquidityInUSD Staked liquidity in USD (V2 Only)
 * @param tokenPrices In a LP, each token price in USD (V2 Only)
 * @returns Farm Apr
 */
export const getFarmAprByVersion = (
  poolWeight: BigNumber,
  vvsPriceUsd: BigNumber,
  poolLiquidityUsd: BigNumber,
  farmAddress: string,
  version: number,
  rewardData: any[],
  stakedLiquidityInUSD: BigNumber,
  tokenPrices: Record<string, number>,
): { vvsRewardsApr: number; tokenRewardsApr: number; lpRewardsApr: number } => {
  if (version === 2) {
    return getFarmAprV2(
      poolWeight,
      vvsPriceUsd,
      rewardData,
      poolLiquidityUsd,
      stakedLiquidityInUSD,
      farmAddress,
      tokenPrices,
    )
  }
  const { vvsRewardsApr, lpRewardsApr } = getFarmApr(poolWeight, vvsPriceUsd, poolLiquidityUsd, farmAddress)
  return {
    vvsRewardsApr,
    tokenRewardsApr: vvsRewardsApr,
    lpRewardsApr,
  }
}

export default null
